import React from 'react';
import { Modal, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

// --- TYPES ---
export type ColorKey = 'purple' | 'green' | 'orange';

export interface TimelineEvent {
  id: number | string;
  time: string;
  duration: string;
  title: string;
  description: string;
  colorKey: ColorKey;
  status: 'completed' | 'active' | 'upcoming';
}

export interface DailyTimeline {
  date: string;
  events: TimelineEvent[];
}

// --- CONFIGURATION ---
const STATUS_COLORS: Record<ColorKey, {
  bg: string;
  border: string;
  circle: string;
  circleBorder: string;
  iconColor: string;
}> = {
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-800',
    circle: 'bg-purple-800',
    circleBorder: 'border-purple-800',
    iconColor: 'text-white'
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-600',
    circle: 'bg-white',
    circleBorder: 'border-green-600',
    iconColor: 'text-green-600'
  },
  orange: {
    bg: 'bg-orange-50',
    border: 'border-orange-500',
    circle: 'bg-white',
    circleBorder: 'border-orange-500',
    iconColor: 'text-orange-500'
  },
};

// --- SUB-COMPONENT: TIMELINE ITEM ---
const TimelineItem: React.FC<{ event: TimelineEvent; isLast: boolean }> = ({ event, isLast }) => {
  const config = STATUS_COLORS[event.colorKey];

  // Parse time to split into time and period (AM/PM)
  const parseTime = (timeStr: string) => {
    const match = timeStr.match(/(\d{1,2}:\d{2})\s*(AM|PM)/i);
    if (match) {
      return { time: match[1], period: match[2].toUpperCase() };
    }
    // If no AM/PM, try to parse 24-hour format
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      const hour = parseInt(parts[0]);
      const minute = parts[1];
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return { time: `${hour12}:${minute}`, period };
    }
    return { time: timeStr, period: '' };
  };

  const { time: timeValue, period } = parseTime(event.time);

  return (
    <div className="flex group min-h-[100px]">
      <div className="flex flex-col items-center w-16 mr-4">
        <div className="flex flex-col items-center mb-1">
          <span className="text-[13px] font-medium text-gray-700 leading-tight">{timeValue}</span>
          {period && <span className="text-[13px] font-medium text-gray-700 leading-tight">{period}</span>}
        </div>

        <div className={`z-10 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${config.circleBorder} ${config.circle}`}>
          {event.status === 'completed' && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
          {event.status === 'active' && <div className="w-2 h-2 rounded-full bg-green-600" />}
        </div>

        {!isLast && (
          <div className="w-[2px] flex-grow border-l-2 border-dashed border-gray-300 my-1" />
        )}
      </div>

      <div className={`flex-grow mb-6 p-4 rounded-lg border-l-[6px] shadow-sm ${config.bg} ${config.border}`}>
        <div className="flex flex-col">
          <h4 className="text-[15px] font-bold text-gray-800">{event.title}</h4>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[12px] text-gray-500 font-medium">{event.time}</span>
            <span className="text-[12px] text-gray-400">{event.duration}</span>
          </div>
          <p className="text-[13px] text-gray-500 mt-2 line-clamp-1 italic">
            {event.description}
          </p>
        </div>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
interface ScheduleTimelineProps {
  visible: boolean;
  onClose: () => void;
  data: DailyTimeline[];
  titleRange: string;
  onAddInterview?: (date: string) => void;
}

const ScheduleTimelineModal: React.FC<ScheduleTimelineProps> = ({ visible, onClose, data, titleRange, onAddInterview }) => {
  const handleAddInterview = () => {
    if (data.length > 0 && data[0].date) {
      // Extract date from the first timeline entry
      const dateStr = data[0].date;
      if (onAddInterview) {
        onAddInterview(dateStr);
      }
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        <div className="flex justify-between items-center w-full pr-8">
          <span className="text-blue-500 font-bold text-lg flex-1 truncate">{titleRange}</span>
          {onAddInterview && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddInterview}
              size="small"
              className="ml-2 shrink-0"
            >
              Add Interview
            </Button>
          )}
        </div>
      }
      footer={null}
      width={600}
      centered
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto', padding: '24px' } }}
    >
      <div className="flex flex-col gap-6 py-2">
        {data.map((day, dayIdx) => (
          <div key={day.date}>
            <h3 className="text-[14px] font-semibold text-gray-700 mb-4">{day.date}</h3>
            <div className="pl-2">
              {day.events.length > 0 ? (
                day.events.map((event, eventIdx) => (
                  <TimelineItem
                    key={event.id}
                    event={event}
                    isLast={dayIdx === data.length - 1 && eventIdx === day.events.length - 1}
                  />
                ))
              ) : (
                <div className="text-center text-gray-400 py-8">No interviews scheduled for this date</div>
              )}
            </div>
            {dayIdx !== data.length - 1 && (
              <div className="border-t border-dotted border-gray-200 mt-4 mb-6" />
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default ScheduleTimelineModal;